// ConfigurationSummary.js
// =============================================================================
// Head Office > "Configuration summary" — a setup checklist, not an activity
// dashboard. The Head Office cards (AppraisalsCard, MhraAlertsCard,
// SurveysCard, etc.) already answer "is this being used, and by how many
// sites" — this page answers a different question: for every place a
// manager can type an email address, flip a toggle, or pick a schedule,
// has that actually been done? Every row is clickable and lands directly
// on the screen that setting lives on.
//
// Backed by one endpoint — POST /api/config-summary (config_summary_routes.js)
// — which does all the aggregation server-side so this component is just
// display + click-through.
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";

const API_BASE = "https://api.pharmacegroup.co.uk";
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";

async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body || {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const S = {
  wrap: { padding: 16, fontFamily: "system-ui, sans-serif" },
  h1: { fontSize: 20, fontWeight: 700, margin: "0 0 4px", color: "#111827" },
  sub: { fontSize: 13, color: "#6b7280", margin: "0 0 16px" },
  group: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 14, overflow: "hidden" },
  groupHeader: { padding: "10px 14px", fontWeight: 700, fontSize: 14, color: "#111827",
                 background: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
         padding: "10px 14px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" },
  rowLast: { borderBottom: "none" },
  label: { fontSize: 13.5, color: "#374151" },
  detail: { fontSize: 12.5, color: "#6b7280" },
  err: { fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca",
         borderRadius: 6, padding: "8px 12px", marginBottom: 12 },
  legend: { display: "flex", gap: 16, alignItems: "center", marginBottom: 14, fontSize: 12, color: "#6b7280" }
};

// Three-value status → dot colour + short readable word. Kept deliberately
// simple (see config_summary_routes.js) so the whole page scans at a glance.
const STATUS_STYLE = {
  ok:      { dot: "#22c55e", word: "Set up" },
  partial: { dot: "#f59e0b", word: "Partly set up" },
  none:    { dot: "#ef4444", word: "Not set up" }
};

function StatusDot({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.none;
  return (
    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%",
                    background: s.dot, marginRight: 8, flexShrink: 0 }}
          title={s.word} />
  );
}

function ItemRow({ item, isLast, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ ...S.row, ...(isLast ? S.rowLast : {}), background: hover ? "#faf5ff" : "#fff" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      title="Click to go to this setting"
    >
      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <StatusDot status={item.status} />
        <span style={S.label}>{item.label}</span>
      </div>
      <span style={S.detail}>{item.detail}</span>
    </div>
  );
}

export default function ConfigurationSummary({ token: tokenProp, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await apiPost("/api/config-summary", {});
      setData(r);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalItems = data ? data.groups.reduce((n, g) => n + g.items.length, 0) : 0;
  const notSetCount = data ? data.groups.reduce((n, g) => n + g.items.filter(i => i.status === "none").length, 0) : 0;

  return (
    <div style={S.wrap}>
      <div style={S.h1}>Configuration summary</div>
      <div style={S.sub}>
        What's been set up across the platform, and what hasn't — every row links straight to the screen it's set from.
        {data && !loading && (
          <> {notSetCount > 0
            ? ` ${notSetCount} of ${totalItems} setting${totalItems === 1 ? "" : "s"} still need${notSetCount === 1 ? "s" : ""} attention.`
            : ` All ${totalItems} settings are set up.`}</>
        )}
      </div>

      <div style={S.legend}>
        <span><StatusDot status="ok" /> Set up</span>
        <span><StatusDot status="partial" /> Partly set up</span>
        <span><StatusDot status="none" /> Not set up</span>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>}

      {data && data.groups.map((g) => (
        <div key={g.key} style={S.group}>
          <div style={S.groupHeader}>{g.label}</div>
          {g.items.map((item, i) => (
            <ItemRow key={item.label} item={item} isLast={i === g.items.length - 1}
                     onClick={() => onNavigate && onNavigate(g.navigate)} />
          ))}
        </div>
      ))}
    </div>
  );
}
